/**
 * WorkflowCellRenderer — renders a single QueueEntry cell from a UiColumnSpec.
 *
 * Owner requirement #1: the Generic Page NEVER checks `if (workflow===...)`.
 * Every column renders generically from its declared `type`. A workflow's
 * personality lives in the Registry, not in this file.
 */

import { Badge } from '@shopify/polaris'
import type { QueueEntry, UiColumnSpec, StatusSet } from '../../sdk/engine.api'

const PHASE_TONE: Record<string, React.ComponentProps<typeof Badge>['tone']> = {
  queued:     undefined,
  assigned:   'info',
  in_session: 'warning',
  escalated:  'critical',
  resolved:   'success',
  archived:   undefined,
  created:    undefined,
}

const PRIORITY_TONE: Record<string, React.ComponentProps<typeof Badge>['tone']> = {
  critical: 'critical',
  high:     'attention',
  normal:   undefined,
}

function formatAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const h  = Math.floor(ms / 3_600_000)
  const m  = Math.floor((ms % 3_600_000) / 60_000)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function formatDatetime(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function truncate(s: string | undefined, max = 30): string {
  if (!s) return '—'
  return s.length > max ? s.slice(0, max) + '…' : s
}

function getValue(entry: QueueEntry, col: UiColumnSpec): unknown {
  if (col.source === 'orderRef') return (entry.orderRef as Record<string, unknown>)[col.key]
  if (col.source === 'meta')    return entry.meta[col.key]
  return (entry as unknown as Record<string, unknown>)[col.key]
}

type Props = {
  col:       UiColumnSpec
  entry:     QueueEntry
  statusSet: StatusSet
}

export function WorkflowCellRenderer({ col, entry, statusSet }: Props) {
  const raw = getValue(entry, col)

  switch (col.type) {
    case 'order_link':
      return (
        <span style={{ color: '#2c6ecb', fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          {String(raw ?? entry.orderRef.orderName)}
        </span>
      )

    case 'status_badge': {
      const sid  = String(raw ?? entry.status)
      const def  = statusSet.statuses.find(s => s.id === sid)
      const tone = def?.color === 'success' ? 'success'
                 : def?.color === 'warning' ? 'warning'
                 : def?.color === 'critical' ? 'critical'
                 : def?.color === 'info'    ? 'info'
                 : undefined
      return <Badge tone={tone}>{def?.name ?? sid.replace(/_/g, ' ')}</Badge>
    }

    case 'phase_badge':
      return (
        <Badge tone={PHASE_TONE[entry.phase]}>
          {entry.phase.replace(/_/g, ' ')}
        </Badge>
      )

    case 'agent':
      return <span style={{ color: '#6d7175' }}>{String(raw ?? '—')}</span>

    case 'datetime':
      return <span style={{ color: '#6d7175', whiteSpace: 'nowrap' }}>{raw ? formatDatetime(String(raw)) : '—'}</span>

    case 'relative_time':
      return <span style={{ color: '#6d7175', whiteSpace: 'nowrap' }}>{raw ? formatAge(String(raw)) : '—'}</span>

    case 'attempts': {
      const a = entry.attempts
      if (!a) return <span style={{ color: '#6d7175' }}>—</span>
      return (
        <span style={{ color: '#6d7175', whiteSpace: 'nowrap' }}>
          {`Day ${a.day} · ${a.today}/${a.dailyQuota}`}
        </span>
      )
    }

    case 'note':
      return (
        <span style={{ color: '#6d7175' }} title={String(raw ?? '')}>
          {raw ? truncate(String(raw), 30) : '—'}
        </span>
      )

    case 'priority': {
      const p = String(raw ?? entry.priority ?? 'normal')
      if (p === 'normal') return <span style={{ color: '#6d7175' }}>—</span>
      return <Badge tone={PRIORITY_TONE[p]}>{p.toUpperCase()}</Badge>
    }

    default:
      return <span style={{ color: '#6d7175' }}>{String(raw ?? '—').replace(/_/g, ' ')}</span>
  }
}

// Column width in pixels — keeps table consistent across workflows.
export function colWidth(col: UiColumnSpec): number {
  switch (col.width) {
    case 'xs': return 60
    case 'sm': return 120
    case 'md': return 180
    case 'lg': return 260
    default:   return 140
  }
}
